<template>
  <div
    :id="id"
    ref="container"
    :class="['modal', modalClass, { fade: !noFade }]"
    tabindex="-1"
    role="dialog"
    aria-hidden="true"
    @click.self="onBackdropClick"
  >
    <div :class="['modal-dialog', ...dialogClasses]" role="document">
      <div :class="['modal-content', contentClass]">
        <!-- HEADER -->
        <div v-if="!hideHeader" class="modal-header">
          <slot name="modal-header" :close="hide"></slot>
          <button
            v-if="!hasCustomClose"
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="hide"
          ></button>
        </div>
        <!-- BODY -->
        <div :class="['modal-body', bodyClass]">
          <slot></slot>
        </div>
        <!-- FOOTER -->
        <div v-if="!hideFooter" class="modal-footer">
          <slot name="modal-footer" :close="hide"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";

export default {
  name: "BaseModal",
  inheritAttrs: false,

  props: {
    id: { type: String, default: "" },
    visible: { type: Boolean, default: false },
    hideHeader: { type: Boolean, default: false },
    hideFooter: { type: Boolean, default: false },
    modalClass: { type: String, default: "" },
    dialogClass: { type: String, default: "" },
    contentClass: { type: String, default: "" },
    bodyClass: { type: String, default: "" },
    noCloseOnBackdrop: { type: Boolean, default: false },
    noFade: { type: Boolean, default: false },
    backdrop: { type: [Boolean, String], default: true },
    keyboard: { type: Boolean, default: true },
    centered: { type: Boolean, default: false },
    size: { type: String, default: "" },
    scrollable: { type: Boolean, default: false },
  },
  data() {
    return { bsInstance: null };
  },
  computed: {
    dialogClasses() {
      const cls = [];
      if (this.dialogClass) cls.push(this.dialogClass);
      if (this.centered) cls.push("modal-dialog-centered");
      if (this.scrollable) cls.push("modal-dialog-scrollable");
      if (this.size) cls.push(`modal-${this.size}`);
      return cls;
    },
    hasCustomClose() {
      return !!(
        this.$slots["modal-header"] || this.$scopedSlots["modal-header"]
      );
    },
  },

  mounted() {
    // initialize Bootstrap modal instance
    this.bsInstance = new Modal(this.$refs.container, {
      backdrop: this.noCloseOnBackdrop ? "static" : this.backdrop,
      keyboard: this.keyboard,
      focus: true,
      show: false,
    });
    // when Bootstrap fires its "hidden" event, remove the element from the body
    this.$refs.container.addEventListener("hidden.bs.modal", () => {
      this.$emit("update:visible", false);

      const remainingShownModals =
        document.querySelectorAll(".modal.show").length;
      if (remainingShownModals === 0) {
        document
          .querySelectorAll(".modal-backdrop")
          .forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
      }

      const c = this.$refs.container;
      // Only remove if the element still exists and is in <body>
      if (c && c.parentNode === document.body) {
        document.body.removeChild(c);
      }
    });
    // sync events
    ["show", "shown", "hide", "hidden"].forEach((evt) => {
      this.$refs.container.addEventListener(`${evt}.bs.modal`, (e) => {
        this.$emit(evt, e);
        if (evt === "shown") {
          this.$emit("update:visible", true);
          this.adjustBackdropOpacity();
        }
        if (evt === "hidden") this.$emit("update:visible", false);
      });
    });

    // auto-show if prop initial true
    if (this.visible) {
      this.show();
    }

    // global bus
    this._onShow = (id) => id === this.id && this.show();
    this._onHide = (id) => id === this.id && this.hide();

    this.$root.$on("base-modal:show", this._onShow);
    this.$root.$on("base-modal:hide", this._onHide);
  },
  beforeDestroy() {
    if (this.bsInstance) {
      this.bsInstance.dispose();
    }
    if (this._onShow) this.$root.$off("base-modal:show", this._onShow);
    if (this._onHide) this.$root.$off("base-modal:hide", this._onHide);
  },
  methods: {
    show() {
      // never try to show before the element is even mounted
      const container = this.$refs.container;
      if (!container || !this.bsInstance || this.bsInstance._isShown) {
        return;
      }

      // append if needed
      if (container.parentNode !== document.body) {
        document.body.appendChild(container);
      }
      // open it
      this.bsInstance.show();

      this.$nextTick(() => {
        this.adjustBackdropOpacity();
        // restack immediately
        const openCount = document.querySelectorAll(".modal.show").length;
        const backdropZ = 1040 + openCount * 20;
        const modalZ = 1050 + openCount * 20;

        container.style.zIndex = modalZ;

        const bd = this.getTopBackdrop();
        if (bd) bd.style.zIndex = backdropZ;
      });
    },
    getTopBackdrop() {
      const backdrops = document.querySelectorAll(".modal-backdrop");
      return backdrops.length ? backdrops[backdrops.length - 1] : null;
    },
    hide() {
      const bd = this.getTopBackdrop();
      if (bd) {
        bd.style.opacity = "0";
      }

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      this.bsInstance.hide();
    },
    onBackdropClick() {
      if (this.noCloseOnBackdrop) return;
      if (this.backdrop) this.hide();
    },
    adjustBackdropOpacity() {
      const bd = this.getTopBackdrop();
      if (bd) {
        bd.style.transition = "opacity 0.3s ease";
        bd.style.opacity = "0.8";
      }
    },
  },
};
</script>

<style scoped>
.modal {
  transition: opacity 0.15s linear;
}
.modal.show {
  display: block;
}
</style>
